import React, { useState } from "react";
import styled from "styled-components";
import { auth } from "../firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  sendEmailVerification,
} from "firebase/auth";

// Expand as needed or import a maintained list
const DISPOSABLE_DOMAINS = [
  "mailinator.com", "tempmail.com", "10minutemail.com", "guerrillamail.com", "yopmail.com"
];
const RESERVED_DOMAINS = [
  "localhost", "127.0.0.1", "example.com", "test.com", "invalid", "example"
];
const FAKE_EMAILS = [
  "test@test.com", "admin@admin.com", "user@user.com"
];

function isValidEmail(email) {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return false;
  const domain = email.split("@")[1]?.toLowerCase();
  if (!domain) return false;
  if (RESERVED_DOMAINS.includes(domain)) return false;
  if (DISPOSABLE_DOMAINS.some(d => domain === d || domain.endsWith("." + d))) return false;
  if (FAKE_EMAILS.includes(email.toLowerCase())) return false;
  return true;
}

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
`;

const Form = styled.form`
  text-align: center;
  background: #f7f7f7;
  padding: 32px 24px;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
`;

const Input = styled.input`
  margin: 8px;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #ddd;
  width: 220px;
`;

const SubmitButton = styled.button`
  margin: 12px;
  padding: 12px 32px;
  border-radius: 24px;
  background: #FFD166;
  border: none;
  font-weight: 600;
  cursor: pointer;
`;

const GoogleButton = styled.button`
  background: #4285F4;
  color: #fff;
  border: none;
  border-radius: 24px;
  padding: 12px 32px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 18px;
  width: 80%;
  max-width: 260px;
`;

const Divider = styled.div`
  margin: 24px 0 12px 0;
  color: #aaa;
  font-size: 0.95em;
`;

export default function AuthScreen({ mode = "login", onAuthSuccess = () => {} }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const isLogin = mode === "login";

  const handleAuth = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");

    // Email validation
    if (!isValidEmail(email)) {
      setError("Please enter a valid, non-temporary email address (e.g., you@example.com).");
      return;
    }

    try {
      if (isLogin) {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        if (!userCredential.user.emailVerified) {
          setError("Please verify your email before logging in. Check your inbox for a verification link.");
          // Optionally, re-send verification email:
          await sendEmailVerification(userCredential.user);
          setInfo("Verification email sent again. Please check your inbox.");
          return;
        }
      } else {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        await sendEmailVerification(userCredential.user);
        setInfo("Sign up successful! Please check your email and verify your address before logging in.");
        return; // Do not call onAuthSuccess until verified
      }
      onAuthSuccess();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    setInfo("");
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      onAuthSuccess();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Wrapper>
      <Form onSubmit={handleAuth}>
        <h2>{isLogin ? "Log In" : "Sign Up"}</h2>
        <Input
          type="email"
          placeholder="Email"
          value={email}
          onChange={e => setEmail(e.target.value.trim())}
          required
          autoComplete="username"
        />
        <br />
        <Input
          type="password"
          placeholder="Password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          required
          autoComplete={isLogin ? "current-password" : "new-password"}
        />
        <br />
        <SubmitButton type="submit">
          {isLogin ? "Log In" : "Sign Up"}
        </SubmitButton>
        <Divider>or</Divider>
        <GoogleButton type="button" onClick={handleGoogleSignIn}>
          Continue with Google
        </GoogleButton>
        {error && <div style={{ color: "red", marginTop: 8 }}>{error}</div>}
        {info && <div style={{ color: "#217a53", marginTop: 8 }}>{info}</div>}
      </Form>
    </Wrapper>
  );
}
